import React from 'react'
import { getCLS, getFID, getFCP, getLCP, getTTFB, Metric } from 'web-vitals'

// Performance monitoring utilities
export class PerformanceMonitor {
  private static instance: PerformanceMonitor
  private metrics: Map<string, number> = new Map()
  private webVitalsMetrics: Map<string, Metric> = new Map()

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor()
    }
    return PerformanceMonitor.instance
  }

  startTimer(label: string): void {
    this.metrics.set(label, performance.now())
  }

  endTimer(label: string): number {
    const start = this.metrics.get(label)
    if (!start) {
      console.warn(`Timer '${label}' was not started`)
      return 0
    }
    
    const duration = performance.now() - start
    this.metrics.delete(label)
    
    // Log slow operations in development
    if (process.env.NODE_ENV === 'development' && duration > 1000) {
      console.warn(`Slow operation: ${label} took ${duration.toFixed(2)}ms`)
    }
    
    // Send to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      this.logPerformanceMetric(label, duration)
    }
    
    return duration
  }

  measureAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
    this.startTimer(label)
    return fn().finally(() => {
      this.endTimer(label)
    })
  }

  measureSync<T>(label: string, fn: () => T): T {
    this.startTimer(label)
    try {
      return fn()
    } finally {
      this.endTimer(label)
    }
  }

  private logPerformanceMetric(label: string, duration: number) {
    // TODO: Send to monitoring service (e.g., DataDog, New Relic, etc.)
    console.log(`Performance: ${label} = ${duration.toFixed(2)}ms`)
  }

  private logWebVital(metric: Metric) {
    this.webVitalsMetrics.set(metric.name, metric)
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`Web Vital - ${metric.name}:`, metric)
    } else {
      // TODO: Send to analytics service
      this.logWebVitalToService(metric)
    }
  }

  private logWebVitalToService(metric: Metric) {
    // TODO: Integrate with analytics service
    console.log('Web Vital logged:', {
      name: metric.name,
      value: metric.value,
      rating: (metric as any).rating,
      delta: metric.delta
    })
  }

  // Initialize Web Vitals monitoring
  initWebVitals() {
    if (typeof window === 'undefined') return

    getCLS(this.logWebVital.bind(this))
    getFID(this.logWebVital.bind(this))
    getFCP(this.logWebVital.bind(this))
    getLCP(this.logWebVital.bind(this))
    getTTFB(this.logWebVital.bind(this))
  }

  // Get current Web Vitals metrics
  getWebVitalsReport(): Record<string, Metric> {
    const report: Record<string, Metric> = {}
    this.webVitalsMetrics.forEach((metric, name) => {
      report[name] = metric
    })
    return report
  }

  // Performance observer for custom metrics
  observePerformance() {
    if (!('PerformanceObserver' in window)) {
      console.warn('PerformanceObserver not supported')
      return
    }

    // Observe navigation timing
    if (PerformanceObserver.supportedEntryTypes.includes('navigation')) {
      const navObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries() as PerformanceNavigationTiming[]
        entries.forEach((entry) => {
          this.logNavigationTiming(entry)
        })
      })
      navObserver.observe({ entryTypes: ['navigation'] })
    }

    // Observe resource timing
    if (PerformanceObserver.supportedEntryTypes.includes('resource')) {
      const resourceObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries() as PerformanceResourceTiming[]
        entries.forEach((entry) => {
          this.logResourceTiming(entry)
        })
      })
      resourceObserver.observe({ entryTypes: ['resource'] })
    }

    // Observe long tasks
    if (PerformanceObserver.supportedEntryTypes.includes('longtask')) {
      const longTaskObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries()
        entries.forEach((entry) => {
          console.warn(`Long task detected: ${entry.duration}ms`)
          this.logPerformanceMetric('long-task', entry.duration)
        })
      })
      longTaskObserver.observe({ entryTypes: ['longtask'] })
    }
  }

  private logNavigationTiming(entry: PerformanceNavigationTiming) {
    const metrics = {
      'page-load': entry.loadEventEnd - entry.startTime,
      'dom-content-loaded': entry.domContentLoadedEventEnd - entry.startTime,
      'dns-lookup': entry.domainLookupEnd - entry.domainLookupStart,
      'tcp-connect': entry.connectEnd - entry.connectStart,
      'server-response': entry.responseEnd - entry.requestStart,
      'dom-processing': entry.domComplete - entry.responseEnd
    }

    Object.entries(metrics).forEach(([name, value]) => {
      if (value > 0) {
        this.logPerformanceMetric(name, value)
      }
    })
  }

  private logResourceTiming(entry: PerformanceResourceTiming) {
    // Log slow resource loads
    const duration = entry.responseEnd - entry.requestStart
    if (duration > 1000) { // Log resources taking more than 1s
      this.logPerformanceMetric(`slow-resource-${entry.initiatorType}`, duration)
    }
  }

  // Memory usage monitoring
  observeMemoryUsage() {
    if (!('memory' in performance)) {
      console.warn('Memory API not supported')
      return
    }

    const logMemoryUsage = () => {
      const memory = (performance as any).memory
      const metrics = {
        'memory-used': memory.usedJSHeapSize,
        'memory-total': memory.totalJSHeapSize,
        'memory-limit': memory.jsHeapSizeLimit
      }

      Object.entries(metrics).forEach(([name, value]) => {
        this.logPerformanceMetric(name, value)
      })

      // Warn about high memory usage
      if (memory.usedJSHeapSize / memory.jsHeapSizeLimit > 0.8) {
        console.warn('High memory usage detected')
      }
    }

    // Log memory usage every 30 seconds
    setInterval(logMemoryUsage, 30000)
    logMemoryUsage() // Initial log
  }
}

// React hook for monitoring component render performance
export function useRenderMetrics(componentName: string) {
  React.useEffect(() => {
    const monitor = PerformanceMonitor.getInstance()
    monitor.startTimer(`${componentName}-render`)
    
    return () => {
      monitor.endTimer(`${componentName}-render`)
    }
  })
}

// Hook for monitoring async operations
export function useAsyncMetrics() {
  const monitor = PerformanceMonitor.getInstance()
  
  const measureAsync = <T>(label: string, fn: () => Promise<T>): Promise<T> => {
    return monitor.measureAsync(label, fn)
  }

  const measureSync = <T>(label: string, fn: () => T): T => {
    return monitor.measureSync(label, fn)
  }

  return { measureAsync, measureSync }
}

// Hook for tracking page views and user interactions
export function useAnalytics() {
  const trackPageView = (pageName: string) => {
    const monitor = PerformanceMonitor.getInstance()
    monitor.startTimer(`page-view-${pageName}`)
    
    // TODO: Send to analytics service
    if (process.env.NODE_ENV === 'production') {
      console.log('Page view:', pageName)
    }
  }

  const trackUserAction = (action: string, data?: Record<string, any>) => {
    const timestamp = performance.now()
    
    // TODO: Send to analytics service
    if (process.env.NODE_ENV === 'production') {
      console.log('User action:', { action, data, timestamp })
    }
  }

  const trackError = (error: Error, context?: string) => {
    const timestamp = performance.now()
    
    // TODO: Send to error tracking service
    console.error('Tracked error:', {
      message: error.message,
      stack: error.stack,
      context,
      timestamp
    })
  }

  return {
    trackPageView,
    trackUserAction,
    trackError
  }
}

// Initialize monitoring when the app starts
export function initializeMonitoring() {
  if (typeof window === 'undefined') return

  const monitor = PerformanceMonitor.getInstance()
  
  // Initialize Web Vitals
  monitor.initWebVitals()
  
  // Start performance observers
  monitor.observePerformance()
  monitor.observeMemoryUsage()
  
  console.log('Performance monitoring initialized')
}

// Utility function to create performance budgets
export function createPerformanceBudget() {
  const budget = {
    // Web Vitals thresholds (good ratings)
    LCP: 2500, // Largest Contentful Paint
    FID: 100,  // First Input Delay
    CLS: 0.1,  // Cumulative Layout Shift
    FCP: 1800, // First Contentful Paint
    TTFB: 800, // Time to First Byte
    
    // Custom metrics
    'page-load': 3000,
    'dom-content-loaded': 1500,
    'api-response': 500,
    'component-render': 16 // 60fps target
  }

  return {
    budget,
    checkBudget: (metricName: string, value: number): boolean => {
      const threshold = budget[metricName as keyof typeof budget]
      if (threshold && value > threshold) {
        console.warn(`Performance budget exceeded: ${metricName} = ${value}ms (budget: ${threshold}ms)`)
        return false
      }
      return true
    }
  }
}

// Types for performance monitoring
export interface PerformanceMetric {
  name: string
  value: number
  timestamp: number
  context?: Record<string, any>
}

export interface WebVitalMetric extends PerformanceMetric {
  rating: 'good' | 'needs-improvement' | 'poor'
  delta: number
}